<template>
    <div class="ds-ex-page">
        <div class="ds-ex-tabs-bar">
            <div class="ds-ex-tabs ds-app-shell">
                <nav class="ds-ex-tabs-list" role="tablist" aria-label="Биржа смен">
                    <button
                        v-for="item in visibleTabs"
                        :key="item.id"
                        type="button"
                        role="tab"
                        class="ds-ex-tab-link"
                        :class="{ 'is-active': activeTab === item.id }"
                        :aria-selected="activeTab === item.id"
                        @click="selectTab(item.id)"
                    >
                        {{ item.label }}
                        <span v-if="item.count" class="ds-ex-count is-dark">{{ item.count }}</span>
                    </button>
                </nav>
                <div class="ds-ex-spacer"></div>
                <span v-if="EXCHANGE_MOCK_ENABLED" class="ds-ex-mock-badge" title="VITE_EXCHANGE_MOCK=scenario: синтетические данные, запросы в backend не отправляются">Демо-данные</span>
                <button v-if="canReport" type="button" class="ds-ex-report-btn" @click="showReport = true">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><path d="M14 3v6h6M9 15h7m-3-3 3 3-3 3" /></svg>
                    Выгрузить отчёт
                </button>
            </div>
        </div>

        <main class="ds-ex-main ds-app-shell">
            <VacanciesTab v-if="visited.vacancies" v-show="activeTab === 'vacancies'" ref="vacanciesTab" :active="activeTab === 'vacancies'" @open="openPanel" @remove="askRemove" />
            <RequestsTab v-if="visited.requests" v-show="activeTab === 'requests'" ref="requestsTab" :active="activeTab === 'requests'" @open="openPanel" @remove="askRemove" />
            <ArchiveTab v-if="visited.archive" v-show="activeTab === 'archive'" ref="archiveTab" :active="activeTab === 'archive'" @open="openPanel" />
            <div v-if="!visibleTabs.length" class="ds-ex-state">Нет прав на просмотр биржи</div>
        </main>

        <SidePanel ref="panel" @close="panelVacancy = null">
            <VacancyPanel
                v-if="panelVacancy"
                :key="panelVacancy.id"
                :vacancy="panelVacancy"
                :initial-tab="panelTab"
                :readonly="panelReadonly"
                :can-manage="canManage"
                @close="closePanel"
                @changed="refreshActive"
                @remove="askRemove(panelVacancy)"
                @to-responses="toResponses"
            />
        </SidePanel>

        <VacancyRemoveDialog v-if="removing" :vacancy="removing" :reason-demanded="reasonDemanded" :after-removed="afterRemoved" @close="removing = null" />
        <ExchangeReportDialog v-if="showReport" @close="showReport = false" />
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import SidePanel from '@/components/schedule/SidePanel.vue'
import VacanciesTab from '@/components/exchange/VacanciesTab.vue'
import RequestsTab from '@/components/exchange/RequestsTab.vue'
import ArchiveTab from '@/components/exchange/ArchiveTab.vue'
import VacancyPanel from '@/components/exchange/VacancyPanel.vue'
import VacancyRemoveDialog from '@/components/exchange/VacancyRemoveDialog.vue'
import ExchangeReportDialog from '@/components/exchange/ExchangeReportDialog.vue'
import dayjs from 'dayjs'
import { EXCHANGE_MOCK_ENABLED, exchangeApi } from '@/components/exchange/exchangeApi'
import { errorText } from '@/components/exchange/exchangeActions'
import { exchangeToast } from '@/components/exchange/exchangeToasts'
import { timeRange } from '@/components/exchange/exchangeFormat'
import { createExchangePage, toggleIn } from '@/components/exchange/exchangePage'
import { useExchangeGuard } from '@/components/exchange/exchangeActions'
import { exchangeTabCounts } from '@/components/exchange/useExchangeList'
import type { ExchangeError, Vacancy } from '@/components/exchange/exchangeTypes'
import { useAuthStore } from '@/stores/authStore'
import { useDepartmentStore } from '@/stores/departmentStore'

type TabId = 'vacancies' | 'requests' | 'archive'

const authStore = useAuthStore()
const departmentStore = useDepartmentStore()
// Черновики и фильтры вкладок живут до ухода из раздела.
const page = createExchangePage()

// Видимость вкладки не даёт права действия; права действий проверяются внутри вкладок.
const tabs = computed(() => [
    { id: 'vacancies' as TabId, label: 'Мои вакансии', allowed: Boolean(authStore.permissions?.can_view_vacancies), count: exchangeTabCounts.vacancies },
    { id: 'requests' as TabId, label: 'Запросы', allowed: Boolean(authStore.permissions?.can_view_vacancy_requests), count: exchangeTabCounts.requests },
    // Архив: list/summary требуют can_view_vacancies (access-matrix, integration-requirements п.3).
    { id: 'archive' as TabId, label: 'Архив', allowed: Boolean(authStore.permissions?.can_view_vacancies), count: null },
])
const visibleTabs = computed(() => tabs.value.filter(item => item.allowed))

// Без разрешённых вкладок ни одна не монтируется и не запрашивает данные.
const activeTab = ref<TabId | null>(visibleTabs.value[0]?.id ?? null)
const visited = reactive<Record<TabId, boolean>>({ vacancies: false, requests: false, archive: false })
if (activeTab.value) visited[activeTab.value] = true

const canReport = computed(() => Boolean(authStore.permissions?.can_download_outsource_report))
const canManage = computed(() => Boolean(authStore.permissions?.can_create_vacancy_exchange))
const showReport = ref(false)

const vacanciesTab = ref<{ refresh: () => Promise<void> } | null>(null)
const requestsTab = ref<{ refresh: () => Promise<void> } | null>(null)
const archiveTab = ref<{ refresh: () => Promise<void> } | null>(null)

function selectTab(id: TabId) {
    visited[id] = true
    activeTab.value = id
}

async function refreshActive() {
    if (!activeTab.value) return
    const tab = { vacancies: vacanciesTab, requests: requestsTab, archive: archiveTab }[activeTab.value]
    await tab.value?.refresh()
}

const panel = ref<{ toggle: () => void, close: () => void } | null>(null)
const panelVacancy = ref<Vacancy | null>(null)
const panelTab = ref<'vacancy' | 'history'>('vacancy')
const panelReadonly = computed(() => activeTab.value === 'archive')

function openPanel(vacancy: Vacancy, tab: 'vacancy' | 'history') {
    panelVacancy.value = vacancy
    panelTab.value = tab
    panel.value?.toggle()
}

function closePanel() {
    panel.value?.close()
    panelVacancy.value = null
}

function toResponses() {
    const vacancy = panelVacancy.value
    if (vacancy) page.expanded = toggleIn(page.expanded, vacancy.id, true)
    closePanel()
}

const removing = ref<Vacancy | null>(null)
const reasonDemanded = ref(false)
const REASON_AFTER = 15 * 60_000

// До 15 минут после создания — снятие сразу, без подтверждения; дольше — диалог с обязательной причиной.
// Порог окончательно проверяет сервер: если он требует причину, открывается тот же диалог.
const { isBusy, guarded } = useExchangeGuard(page)

// Занятость проверяется до диалога и до mutation; блокировка держится до обновления списка.
async function askRemove(vacancy: Vacancy) {
    if (isBusy(vacancy.id)) {
        exchangeToast.error('Дождитесь завершения действия', { text: vacancy.position.name, key: `remove:${vacancy.id}` })
        return
    }
    const fresh = vacancy.createdAt && Date.now() - dayjs(vacancy.createdAt).valueOf() <= REASON_AFTER
    reasonDemanded.value = false
    if (!fresh) {
        removing.value = vacancy
        return
    }
    await guarded(vacancy.id, async () => {
        try {
            await exchangeApi.remove(vacancy.id, '')
        } catch (error) {
            const e = error as ExchangeError
            // Диалог причины — только по code deletion_reason_required, не по любому 409.
            if (e?.code === 'deletion_reason_required') {
                reasonDemanded.value = true
                removing.value = vacancy
                return
            }
            exchangeToast.error(vacancy.isRequest ? 'Не удалось удалить запрос' : 'Не удалось снять вакансию', { text: errorText(error, 'Попробуйте ещё раз'), key: `remove:${vacancy.id}` })
            // Перечитать актуальное состояние (уже удалена, смена создана и т. п.) внутри той же блокировки.
            if (e?.code === 'not_found' && panelVacancy.value?.id === vacancy.id) closePanel()
            await refreshActive()
            return
        }
        exchangeToast.success(vacancy.isRequest ? 'Запрос удалён' : 'Вакансия снята', { text: `${vacancy.position.name} · ${timeRange(vacancy)}`, key: `remove:${vacancy.id}` })
        if (panelVacancy.value?.id === vacancy.id) closePanel()
        await refreshActive()
    })
}

// Вызывается диалогом внутри его блокировки: закрыть панель и дождаться обновления списка.
async function afterRemoved(id: string) {
    if (panelVacancy.value?.id === id) closePanel()
    removing.value = null
    await refreshActive()
}

onMounted(() => {
    if (!departmentStore.myDepartments.length) void departmentStore.fetchMyDepartments()
})
</script>
